/**
 * Axios HTTP Client
 *
 * Pre-configured axios instance for all API calls:
 * - Same-origin baseURL (Vite dev proxy forwards /api, /auth to the backend)
 * - withCredentials for session cookies
 * - Request interceptor adds a unique X-Request-Id header
 * - Response interceptor redirects to /login on 401 for authenticated endpoints
 *   (not for /auth/* itself — /auth/me legitimately 401s when not logged in).
 */

import axios from 'axios';

const apiClient = axios.create({
  baseURL: '',
  withCredentials: true,
});

// ---------------------------------------------------------------------------
// Request interceptor — attach a correlation id
// ---------------------------------------------------------------------------
apiClient.interceptors.request.use((config) => {
  const requestId = Math.random().toString(36).substring(2, 10);
  config.headers['X-Request-Id'] = requestId;
  return config;
});

// ---------------------------------------------------------------------------
// Response interceptor — redirect to /login on 401
// ---------------------------------------------------------------------------
// Paths that are allowed to 401 without bouncing the user. /auth/me is probed
// on boot to check login state; /auth/google and /auth/dev-login surface their
// own errors on the login screen; /auth/logout doesn't need a redirect.
const AUTH_PROBE_PATHS = ['/auth/me', '/auth/google', '/auth/dev-login', '/auth/logout'];

// Guard so concurrent 401s don't trigger multiple redirects.
let redirecting = false;

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401 && typeof window !== 'undefined') {
      const reqUrl: string = error.config?.url || '';
      const isAuthProbe = AUTH_PROBE_PATHS.some((p) => reqUrl.includes(p));
      const onLoginPage = window.location.pathname === '/login';

      if (!isAuthProbe && !onLoginPage && !redirecting) {
        redirecting = true;
        // Preserve the page they were on so we can bounce back after login.
        const returnTo = window.location.pathname + window.location.search;
        window.location.replace(`/login?returnTo=${encodeURIComponent(returnTo)}`);
      }
    }
    return Promise.reject(error);
  },
);

export default apiClient;
